import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import {
  Box,
  Button,
  Center,
  Divider,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CircleCheck,
  DotsCircleHorizontal,
  InfoCircle,
} from 'tabler-icons-react';

import { DescriptionList } from '../../components/description-list/description-list';
import { UiCard } from '../../components/ui/ui-card/ui-card';
import { apiClient } from '../../core/libs/api';
import { HttpResource } from '../../core/libs/http-resource';
import { EOrderStatus } from '../../domains/orders/core/order';
import { AppNotify } from '../../features/app-notify/app-notify';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { routeCabinet } from '../../router/routes';
import { getOrderStatusRequest } from './requests/get-order-status.request';

import styles from './order-status.module.css';

export const OrderStatusPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [payment, setPayment] = useState({
    id: 0,
    personId: 0,
    date: new Date().toJSON(),
    status: 0,
    shared: false,
    description: '',
    payId: 0,
    paySumm: 0,
    tripId: 0,
    stationZoneId: 0,
    tickets: [] as { count: number }[],
    ticketQr: '',
    payTerminalId: 0,
    payCheckNumber: 0,
    payType: 0,
    payReturnUrl: '',
    payConfirmationUrl: '',
    payConfirmationStatus: 0,
    payRemoteId: '',
  });

  const [error, setError] = useState('');

  const [orderDetails] = useState(
    () =>
      new HttpResource<string, []>({
        apiClient,
        httpRequest: getOrderStatusRequest,
        errorNotify: new AppNotify(),
        onFinish(result: any) {
          setPayment(result);
        },
        onError(error) {
          setError(error.message);
        },
      })
  );

  const isTicketWaiting = payment.status === EOrderStatus.new;
  const isTicketDone = payment.status === EOrderStatus.success;
  const isTicketBooked = payment.status === EOrderStatus.booked;
  const isTicketError = payment.status === EOrderStatus.error;
  const isTicketCanceled = payment.status === EOrderStatus.canceled;

  useEffect(() => {
    if (!id) return;
    orderDetails.send(id);
  }, [id, orderDetails, searchParams]);

  if (orderDetails.isWaitingForLoad)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>

        <main className={styles.main}>
          <UiCard>
            <Box pos="relative" mih={150}>
              <LoadingOverlay visible />
            </Box>
          </UiCard>
        </main>
      </div>
    );

  if (!id)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>
        <main className={styles.main}>
          <Stack spacing="sm">
            <UiCard>
              <Trans>
                Отсутствует ID платежа. Купленные билеты можно посмотреть в
                разделе "Мои билеты"
              </Trans>
            </UiCard>
            <UiCard>
              <Button
                size="lg"
                fullWidth
                onClick={() => navigate(routeCabinet.TICKETS.path)}
              >
                <Trans>Мои билеты</Trans>
              </Button>
            </UiCard>
          </Stack>
        </main>
      </div>
    );

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.routeTitle}>
          <h1 className={styles.headerTitle}>
            <Trans>Данные заказа</Trans>
          </h1>
        </div>

        <div className={styles.mainSideMenu}>
          <MainSideMenu />
        </div>
      </header>

      <main className={styles.main}>
        <Stack spacing="sm">
          {isTicketError && (
            <>
              <UiCard>
                <Stack spacing="sm">
                  <Center>
                    <AlertCircle size={48} strokeWidth={2} color={'#FF4D4F'} />
                    <Text fw="bold" ml={16}>
                      <Trans>Ошибка при обработке заказа</Trans>
                    </Text>
                  </Center>

                  <Divider />

                  <Text component="p" m={0}>
                    <Trans>При обработке платежа произошла ошибка</Trans>
                  </Text>

                  {error && <p>{error}</p>}
                </Stack>
              </UiCard>

              <UiCard>
                <Stack spacing="sm">
                  <Button
                    size="lg"
                    onClick={() => window.location.reload()}
                    fullWidth
                  >
                    <Trans>Обновить статус</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(routeCabinet.TICKETS.path)}
                    fullWidth
                  >
                    <Trans>Мои билеты</Trans>
                  </Button>
                </Stack>
              </UiCard>
            </>
          )}

          {isTicketWaiting && (
            <>
              <UiCard>
                <Stack spacing="sm">
                  <Center>
                    <DotsCircleHorizontal
                      size={48}
                      strokeWidth={2}
                      color={'#1677FF'}
                    />
                    <Text fw="bold" ml={16}>
                      <Trans>Заказ обрабатывается</Trans>
                    </Text>
                  </Center>
                  <Divider />
                  <Text component="p" m={0}>
                    <Trans>
                      Заказ обрабатывается, ожидаем подтверждения оплаты
                    </Trans>
                    .
                    <p>
                      <Trans>
                        Если у вас не получилось произвести оплату, оплатить
                        заказ можно по ссылке
                      </Trans>
                      :{' '}
                      <a href={payment.payConfirmationUrl}>
                        <Trans>Перейти к оплате</Trans>
                      </a>
                    </p>
                    <Trans>
                      Попробуйте обновить статус, либо проверить купленные
                      билеты в разделе{' '}
                      <Link to={routeCabinet.TICKETS.path}>Мои билеты</Link>
                    </Trans>
                  </Text>
                </Stack>
              </UiCard>

              <UiCard>
                <Stack spacing="sm">
                  <Button
                    size="lg"
                    onClick={() => window.location.reload()}
                    fullWidth
                  >
                    <Trans>Обновить статус</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(routeCabinet.TICKETS.path)}
                    fullWidth
                  >
                    <Trans>Мои билеты</Trans>
                  </Button>
                </Stack>
              </UiCard>
            </>
          )}

          {isTicketBooked && (
            <UiCard>
              <Stack spacing="sm">
                <Center>
                  <DotsCircleHorizontal
                    size={48}
                    strokeWidth={2}
                    color={'#1677FF'}
                  />
                  <Text fw="bold" ml={16}>
                    <Trans>Заказ был забронирован</Trans>
                  </Text>
                </Center>
                <Divider />
                <Text component="p" m={0}>
                  <Trans>
                    Заказ был забронирован и ожидает завершения оплаты
                  </Trans>
                  <br />
                </Text>
              </Stack>
            </UiCard>
          )}

          {isTicketCanceled && (
            <UiCard>
              <Stack spacing="sm">
                <Center>
                  <InfoCircle size={48} strokeWidth={2} color={'#FAAC15'} />
                  <Text fw="bold" ml={16}>
                    <Trans>Заказ был отменен</Trans>
                  </Text>
                </Center>
                <Divider />
                <Text component="p" m={0}>
                  <Trans>
                    Заказ был отменен. Сумма заказа вернется на ваш счет
                  </Trans>
                  <br />
                </Text>
              </Stack>
            </UiCard>
          )}

          {isTicketDone && (
            <>
              <UiCard>
                <Stack spacing="sm">
                  <Center>
                    <CircleCheck size={48} strokeWidth={2} color={'#02A002'} />
                    <Text fw="bold" ml={16}>
                      <Trans>Заказ был успешно оплачен</Trans>
                    </Text>
                  </Center>
                  <Divider />
                  <Text component="p" m={0}>
                    <Trans>
                      Для просмотра вашего билета нажмите на кнопку ниже.
                    </Trans>
                    <br />
                    <Trans>
                      Все оплаченные билеты доступны в разделе{' '}
                      <Link to={routeCabinet.TICKETS.path}>Мои билеты</Link>.
                    </Trans>
                  </Text>
                </Stack>
              </UiCard>

              <UiCard>
                <Button
                  size="lg"
                  onClick={() =>
                    navigate(routeCabinet.TICKET.buildPath({ id: payment.id }))
                  }
                  fullWidth
                >
                  <Trans>Показать билет</Trans>
                </Button>
              </UiCard>
            </>
          )}

          <UiCard>
            <DescriptionList
              list={[
                {
                  label: t`Номер платежа`,
                  value: payment.id ? `# ${payment.id}` : '',
                },
                {
                  label: t`Дата`,
                  value: new Date(payment.date).toLocaleString(),
                },
                { label: t`Описание`, value: payment.description || '' },
                {
                  label: t`Стоимость билетов`,
                  value: payment.paySumm ? `${payment.paySumm} ֏` : '',
                },
                // { label: 'Комиссия', value: `${payment.payCommission} ֏` },
              ]}
            />
          </UiCard>
        </Stack>
      </main>
    </div>
  );
};
